import { DataProvider } from "react-admin";

const apiUrl = process.env.REACT_APP_API_URL;

function flattenFilter(filter: Record<string, any>) {
  const result: Record<string, any> = {};
  for (const key in filter) {
    if (
      typeof filter[key] === "object" &&
      filter[key] !== null &&
      Object.prototype.hasOwnProperty.call(filter[key], "fullName")
    ) {
      // If the filter value is an object with a fullName property, use that string
      result[key] = filter[key].fullName;
    } else {
      result[key] = filter[key];
    }
  }
  return result;
}

const dataProvider: DataProvider = {
  getList: async (resource, params) => {
    const { page, perPage } = params.pagination || { page: 1, perPage: 10 };
    const { field, order } = params.sort || { field: "id", order: "ASC" };
    // Patch: flatten filter values to avoid [object Object]
    const flatFilter = flattenFilter(params.filter || {});
    // Ensure all query values are strings
    const query: Record<string, string> = {
      _page: String(page),
      _limit: String(perPage),
      _sort: String(field),
      _order: String(order),
      ...Object.fromEntries(
        Object.entries(flatFilter).map(([k, v]) => [k, String(v)])
      ),
    };
    const url = `${apiUrl}/${resource}?${new URLSearchParams(
      query
    ).toString()}`;
    const token = localStorage.getItem("auth");
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const json = await response.json();
    return {
      data: json,
      total: parseInt(response.headers.get("X-Total-Count") || "0", 10),
    };
  },

  getOne: async (resource, params) => {
    const url = `${apiUrl}/${resource}/${params.id}`;
    const token = localStorage.getItem("auth");
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const json = await response.json();
    return { data: json };
  },

  create: async (resource, params) => {
    const url = `${apiUrl}/${resource}`;
    const token = localStorage.getItem("auth");
    let headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    let body: any;
    if (params.data instanceof FormData) {
      body = params.data;
      // Do not set Content-Type for FormData
    } else {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(params.data);
    }
    const response = await fetch(url, {
      method: "POST",
      headers,
      body,
    });
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const json = await response.json();
    return { data: json };
  },

  update: async (resource, params) => {
    const url = `${apiUrl}/${resource}/${params.id}`;
    const token = localStorage.getItem("auth");
    let headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    let body: any;
    if (params.data instanceof FormData) {
      body = params.data;
      // Do not set Content-Type for FormData
    } else {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(params.data);
    }
    const response = await fetch(url, {
      method: "PUT",
      headers,
      body,
    });
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const json = await response.json();
    return { data: json };
  },

  delete: async (resource, params) => {
    const url = `${apiUrl}/${resource}/${params.id}`;
    const token = localStorage.getItem("auth");
    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return { data: params.previousData as any };
  },

  getMany: async (resource, params) => {
    const query = {
      id: params.ids.join(","),
    };
    const url = `${apiUrl}/${resource}?${new URLSearchParams(
      query
    ).toString()}`;
    const token = localStorage.getItem("auth");
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const json = await response.json();
    return { data: json };
  },

  getManyReference: async (resource, params) => {
    const { page, perPage } = params.pagination || { page: 1, perPage: 10 };
    const { field, order } = params.sort || { field: "id", order: "ASC" };
    const query = {
      _page: page,
      _limit: perPage,
      _sort: field,
      _order: order,
      [params.target]: params.id,
      ...params.filter,
    };
    const url = `${apiUrl}/${resource}?${new URLSearchParams(
      query
    ).toString()}`;
    const token = localStorage.getItem("auth");
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const json = await response.json();
    return {
      data: json,
      total: parseInt(response.headers.get("X-Total-Count") || "0", 10),
    };
  },

  updateMany: async (resource, params) => {
    const responses = await Promise.all(
      params.ids.map((id) =>
        fetch(`${apiUrl}/${resource}/${id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("auth")}`,
          },
          body: JSON.stringify(params.data),
        })
      )
    );
    if (responses.some((response) => !response.ok)) {
      throw new Error("Network response was not ok");
    }
    return { data: params.ids };
  },

  deleteMany: async (resource, params) => {
    const responses = await Promise.all(
      params.ids.map((id) =>
        fetch(`${apiUrl}/${resource}/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth")}`,
          },
        })
      )
    );
    if (responses.some((response) => !response.ok)) {
      throw new Error("Network response was not ok");
    }
    return { data: params.ids };
  },
};

export default dataProvider;
